/**
 * PATH: apps/hr/src/config/navigation.ts
 * PURPOSE: HR Hub Navigation Configuration
 * 
 * Uses @quotech/ui for unified navigation across all Quotech hubs.
 */

import {
  Users,
  CalendarOff,
  DollarSign,
  Ticket,
  FileText,
  Settings,
  Briefcase,
  Calendar,
  FileEdit,
  Mail,
  Plus,
  UserPlus,
  ClipboardList,
  Clock,
  Inbox,
} from 'lucide-react'
import type { NavItem, ExternalLink, CommandItem, ModuleConfig, HubId, QuickAddItem } from '@quotech/ui'
import { getExternalLinks } from '@quotech/ui'
import type { HrFeatures } from '@quotech/auth'

// =============================================================================
// FEATURE-FLAG METADATA (Phase 2 — HR_UNIFICATION_PLAN.md)
// =============================================================================

/**
 * Maps a NavItem `id` to the HrFeatures key that gates it. Items
 * with no entry are always shown (e.g. Overview). Used by
 * `filterNavItemsByHrFeatures()` so the sidebar hides routes the
 * tenant hasn't enabled.
 *
 * Keep in sync with the gates above each NAV_ITEMS / SECONDARY_NAV_ITEMS entry.
 */
const NAV_FEATURE_KEY: Partial<Record<string, keyof HrFeatures>> = {
  clock: 'timeclock',
  inbox: 'approvals',
  leave: 'leave',
  payroll: 'payroll',
  documents: 'documents',
  compliance: 'compliance',
}

/**
 * Filter nav items by the resolved HR feature flags. An item is
 * kept when:
 *   - it has no feature gate (always-on, e.g. Overview), OR
 *   - the tenant has the matching `HrFeatures.<key>` flag set.
 *
 * The `module` master flag short-circuits everything below it: when
 * `module=false`, only the always-on items render — the rest of the
 * HR module is effectively dark for this tenant.
 */
export function filterNavItemsByHrFeatures(
  items: NavItem[],
  features: HrFeatures,
): NavItem[] {
  if (!features.module) {
    return items.filter((item) => !NAV_FEATURE_KEY[item.id])
  }
  return items.filter((item) => {
    const key = NAV_FEATURE_KEY[item.id]
    if (!key) return true
    return Boolean(features[key])
  })
}

// =============================================================================
// MODULE CONFIG
// =============================================================================

export const MODULE_CONFIG: ModuleConfig = {
  id: 'hr',
  name: 'HR Hub',
  icon: Users,
  accentColor: 'teal',
  accentGradient: 'from-teal-500 to-cyan-600',
  baseUrl: 'https://hr.quotech.ai',
}

// =============================================================================
// MAIN NAVIGATION
// =============================================================================

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'employees',
    label: 'Employees',
    icon: Users,
    href: '/employees',
  },
  // Gated by HrFeatures.timeclock — see filterNavItemsByHrFeatures.
  {
    id: 'clock',
    label: 'Time Clock',
    icon: Clock,
    href: '/clock',
  },
  // Gated by HrFeatures.approvals — supervisor approval queue
  // (Phase 3 of HR_UNIFICATION_PLAN.md).
  {
    id: 'inbox',
    label: 'Approvals',
    icon: Inbox,
    href: '/inbox',
  },
  // Gated by HrFeatures.leave.
  {
    id: 'leave',
    label: 'Leave',
    icon: CalendarOff,
    href: '/leave',
  },
  // Gated by HrFeatures.payroll.
  {
    id: 'payroll',
    label: 'Payroll',
    icon: DollarSign,
    href: '/payroll',
  },
  // Gated by HrFeatures.documents.
  {
    id: 'documents',
    label: 'Documents',
    icon: FileText,
    href: '/documents',
  },
  {
    id: 'support',
    label: 'Support',
    icon: Ticket,
    href: '/support',
  },
]

// =============================================================================
// SECONDARY NAVIGATION
// =============================================================================

export const SECONDARY_NAV_ITEMS: NavItem[] = [
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    href: '/settings',
  },
]

// =============================================================================
// EXTERNAL HUB LINKS
// =============================================================================

export const EXTERNAL_LINKS: ExternalLink[] = getExternalLinks('hr')

// =============================================================================
// QUICK ADD ITEMS
// =============================================================================

export const QUICK_ADD_ITEMS: QuickAddItem[] = [
  {
    id: 'new-employee',
    label: 'Add Employee',
    icon: UserPlus,
    action: () => { window.dispatchEvent(new CustomEvent('hr:add-employee')) },
    shortcut: '⌘⇧E',
  },
  {
    id: 'new-leave',
    label: 'New Leave Request',
    icon: CalendarOff,
    action: () => { window.dispatchEvent(new CustomEvent('hr:add-leave')) },
  },
  {
    id: 'new-ticket',
    label: 'New Support Ticket',
    icon: Ticket,
    action: () => { window.dispatchEvent(new CustomEvent('hr:add-ticket')) },
  },
]

// =============================================================================
// COMMAND PALETTE
// =============================================================================

export const COMMANDS: CommandItem[] = [
  // Actions
  {
    id: 'new-employee',
    label: 'Add New Employee',
    description: 'Create a new employee record',
    icon: UserPlus,
    action: () => { window.dispatchEvent(new CustomEvent('hr:add-employee')) },
    keywords: ['create', 'add', 'employee', 'hire'],
    category: 'action',
  },
  {
    id: 'new-leave',
    label: 'New Leave Request',
    description: 'Create a new leave request',
    icon: CalendarOff,
    action: () => { window.dispatchEvent(new CustomEvent('hr:add-leave')) },
    keywords: ['create', 'add', 'leave', 'vacation', 'time off'],
    category: 'action',
  },
  {
    id: 'new-ticket',
    label: 'New Support Ticket',
    description: 'Create a new HR support ticket',
    icon: Ticket,
    action: () => { window.dispatchEvent(new CustomEvent('hr:add-ticket')) },
    keywords: ['create', 'add', 'ticket', 'support', 'help'],
    category: 'action',
  },
  
  // Navigation
  {
    id: 'go-dashboard',
    label: 'Go to Employees',
    icon: Users,
    action: () => { window.location.href = '/employees' },
    keywords: ['navigate', 'home', 'employees', 'staff'],
    category: 'navigation',
  },
  {
    id: 'go-leave',
    label: 'Go to Leave',
    icon: CalendarOff,
    action: () => { window.location.href = '/leave' },
    keywords: ['navigate', 'leave', 'vacation', 'time off'],
    category: 'navigation',
  },
  {
    id: 'go-payroll',
    label: 'Go to Payroll',
    icon: DollarSign,
    action: () => { window.location.href = '/payroll' },
    keywords: ['navigate', 'payroll', 'salary', 'wages'],
    category: 'navigation',
  },
  
  // Hub switching
  {
    id: 'switch-jobs',
    label: 'Switch to Jobs Hub',
    icon: Briefcase,
    action: () => { window.location.href = 'https://dashboard.quotech.ai' },
    keywords: ['hub', 'jobs', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-workforce',
    label: 'Switch to Workforce Hub',
    icon: Calendar,
    action: () => { window.location.href = 'https://workforce.quotech.ai' },
    keywords: ['hub', 'workforce', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-quote',
    label: 'Switch to Quote Hub',
    icon: FileEdit,
    action: () => { window.location.href = 'https://quote.quotech.ai' },
    keywords: ['hub', 'quote', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-email',
    label: 'Switch to Email Hub',
    icon: Mail,
    action: () => { window.location.href = 'https://email.quotech.ai' },
    keywords: ['hub', 'email', 'switch'],
    category: 'hub',
  },
]

// =============================================================================
// HUB LINKS
// =============================================================================

export const HUB_LINKS: ExternalLink[] = EXTERNAL_LINKS
